import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { streamAI } from "@/lib/ai";
import { useFacility } from "@/context/FacilityContext";
import { printDocument } from "@/lib/print";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Plus, Trash2, Printer, Save, CheckCircle2, Loader2, Activity, X, FlaskConical, Scan, UserPlus, Network, Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const REFERRAL_TYPES = ["Laboratory", "Pharmacy", "Radiology", "Other Facility", "Specialist"];
const FREQ_PRESETS = ["OD", "BD", "TDS", "QID", "HS", "SOS", "1-0-0", "0-1-0", "0-0-1", "1-0-1", "1-1-1"];

export default function Consultation() {
  const { visitId } = useParams();
  const navigate = useNavigate();
  const { has } = useFacility();
  const editable = has("consultation:edit");
  const [visit, setVisit] = useState(null);
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dx, setDx] = useState("");
  const [drugs, setDrugs] = useState([]);
  const [aiInput, setAiInput] = useState("");
  const [aiDraft, setAiDraft] = useState("");
  const [aiStreaming, setAiStreaming] = useState(false);
  const [medCheck, setMedCheck] = useState(null);
  const [medChecking, setMedChecking] = useState(false);

  useEffect(() => {
    if (visit?.facility_id) {
      api.get(`/opd/drug-suggestions?facility_id=${visit.facility_id}`).then((r) => setDrugs(r.data || [])).catch(() => {});
    }
  }, [visit?.facility_id]);

  useEffect(() => {
    api.get(`/opd/visits/${visitId}`).then((r) => {
      setVisit(r.data);
      const ex = r.data.consultation || {};
      setC({
        vitals: ex.vitals || { bp: "", pulse: "", temp: "", spo2: "", rr: "", weight: "", height: "", bmi: "" },
        chief_complaint: ex.chief_complaint ?? r.data.chief_complaint ?? "",
        history_present_illness: ex.history_present_illness || "",
        examination: ex.examination || "", past_history: ex.past_history || "",
        diagnosis: ex.diagnosis || [], notes: ex.notes || "",
        prescription: ex.prescription || [], referrals: ex.referrals || [],
        follow_up_date: ex.follow_up_date || "", follow_up_time: ex.follow_up_time || "", follow_up_advice: ex.follow_up_advice || "",
      });
    }).catch(() => toast.error("Failed to load visit"));
  }, [visitId]);

  const setVital = (k, v) => setC((s) => {
    const vitals = { ...s.vitals, [k]: v };
    const w = parseFloat(vitals.weight), h = parseFloat(vitals.height) / 100;
    vitals.bmi = w && h ? (w / (h * h)).toFixed(1) : "";
    return { ...s, vitals };
  });
  const addRx = () => setC((s) => ({ ...s, prescription: [...s.prescription, { drug: "", dose: "", frequency: "", duration: "", instructions: "" }] }));
  const setRx = (i, k, v) => setC((s) => ({ ...s, prescription: s.prescription.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const delRx = (i) => setC((s) => ({ ...s, prescription: s.prescription.filter((_, idx) => idx !== i) }));
  const addRef = () => setC((s) => ({ ...s, referrals: [...s.referrals, { type: "Laboratory", target: "", notes: "" }] }));
  const setRef = (i, k, v) => setC((s) => ({ ...s, referrals: s.referrals.map((r, idx) => idx === i ? { ...r, [k]: v } : r) }));
  const delRef = (i) => setC((s) => ({ ...s, referrals: s.referrals.filter((_, idx) => idx !== i) }));
  const addDx = () => { if (dx.trim()) { setC((s) => ({ ...s, diagnosis: [...s.diagnosis, dx.trim()] })); setDx(""); } };
  const openCareCompare = () => window.open("https://carecompare.in", "_blank", "noopener,noreferrer");

  const draftWithAi = async () => {
    if (!aiInput.trim()) { toast.error("Type some rough notes first"); return; }
    setAiStreaming(true); setAiDraft("");
    try {
      await streamAI("/ai/consultation-draft", {
        body: { visit_id: visitId, notes: aiInput },
        facilityId: visit?.facility_id,
        onChunk: (t) => setAiDraft((s) => s + t),
      });
    } catch { toast.error("AI draft failed. Please try again."); }
    finally { setAiStreaming(false); }
  };

  const parseDraft = (text) => {
    const keys = ["CHIEF COMPLAINT", "HISTORY OF PRESENT ILLNESS", "EXAMINATION", "PROVISIONAL DIAGNOSIS", "ADVICE"];
    const out = {};
    keys.forEach((k, i) => {
      const m = text.match(new RegExp(k + "\\s*:", "i"));
      if (!m) return;
      const from = m.index + m[0].length;
      let to = text.length;
      keys.forEach((k2, j) => {
        if (j === i) return;
        const mm = text.slice(from).match(new RegExp(k2 + "\\s*:", "i"));
        if (mm) to = Math.min(to, from + mm.index);
      });
      out[k] = text.slice(from, to).trim();
    });
    return out;
  };

  const applyDraft = () => {
    const p = parseDraft(aiDraft);
    if (Object.keys(p).length === 0) {
      setC((s) => ({ ...s, notes: (s.notes ? s.notes + "\n\n" : "") + aiDraft.trim() }));
      toast.success("Draft added to clinical notes — review before saving");
      return;
    }
    setC((s) => {
      const dxText = p["PROVISIONAL DIAGNOSIS"] || "";
      const dxList = dxText ? dxText.split(/[;\n]+/).map((x) => x.replace(/^[-•\d.\s]+/, "").trim()).filter(Boolean) : [];
      const advice = p["ADVICE"] ? "Advice: " + p["ADVICE"] : "";
      return {
        ...s,
        chief_complaint: p["CHIEF COMPLAINT"] || s.chief_complaint,
        history_present_illness: p["HISTORY OF PRESENT ILLNESS"] || s.history_present_illness,
        examination: p["EXAMINATION"] || s.examination,
        diagnosis: dxList.length ? Array.from(new Set([...s.diagnosis, ...dxList])) : s.diagnosis,
        notes: advice ? (s.notes ? s.notes + "\n\n" : "") + advice : s.notes,
      };
    });
    toast.success("Draft inserted — review and edit before saving");
  };

  const runMedCheck = async () => {
    const meds = c.prescription.filter((r) => r.drug && r.drug.trim());
    if (meds.length === 0) { toast.error("Add at least one medicine first"); return; }
    setMedChecking(true); setMedCheck(null);
    try {
      const { data } = await api.post("/ai/med-check", { medicines: meds, patient_id: visit?.patient_id || visit?.patient?.id || null });
      setMedCheck(data);
    } catch { toast.error("Safety check failed. Please try again."); }
    finally { setMedChecking(false); }
  };

  const save = async (complete = false) => {
    setSaving(true);
    try {
      await api.put(`/opd/visits/${visitId}/consultation`, c);
      if (complete) {
        const { data } = await api.patch(`/opd/visits/${visitId}/status`, { status: "completed" });
        toast.success("Consultation saved & visit completed");
        const closed = data?.closed_appointments || [];
        if (closed.length) {
          const a = closed[0];
          toast.info(`Upcoming appointment on ${a.date}${a.start_time ? ` at ${a.start_time}` : ""} was auto-closed — patient seen today.`);
        }
        navigate("/app/opd");
      } else {
        toast.success("Consultation saved");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const printRx = () => {
    const p = visit?.patient || {};
    const rows = c.prescription.map((r) => `<tr><td>${r.drug}</td><td>${r.dose}</td><td>${r.frequency}</td><td>${r.duration}</td><td>${r.instructions}</td></tr>`).join("");
    printDocument("Prescription", `
      <div class="row"><div><h1>${visit?.facility?.name || "Prescription"}</h1><div class="muted">Prescription</div></div>
      <div class="muted" style="text-align:right">${new Date().toLocaleDateString()}<br/>Token #${visit?.token_number}</div></div>
      <hr class="hr"/>
      <div class="row"><div><strong>${p.name}</strong><div class="muted">${p.uhid} · ${p.gender||""}, ${p.age||""}y</div></div>
      <div class="muted">Dr. ${visit?.doctor_name || "—"}</div></div>
      <div class="sec"><h3>Diagnosis</h3>${(c.diagnosis||[]).map((d)=>`<span class="tag">${d}</span>`).join("") || "—"}</div>
      <div class="sec"><h3>Rx</h3><table><thead><tr><th>Drug</th><th>Dose</th><th>Frequency</th><th>Duration</th><th>Instructions</th></tr></thead><tbody>${rows||'<tr><td colspan=5>No medicines</td></tr>'}</tbody></table></div>
      ${c.follow_up_date ? `<div class="sec"><h3>Follow-up</h3>${c.follow_up_date}${c.follow_up_time ? ` at ${c.follow_up_time}` : ""} — ${c.follow_up_advice||""}</div>` : ""}`);
  };

  if (!visit || !c) return <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;
  const p = visit.patient || {};

  return (
    <div data-testid="consultation-page">
      <button onClick={() => navigate("/app/opd")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4" data-testid="back-to-queue"><ArrowLeft className="h-4 w-4" /> Back to queue</button>

      <div className="bg-slate-900 text-white rounded-md p-5 mb-6 flex flex-wrap items-center gap-4">
        <span className="h-11 w-11 rounded-md bg-blue-600 grid place-items-center font-display font-bold">#{visit.token_number}</span>
        <div className="flex-1 min-w-0">
          <div className="font-display text-lg font-semibold">{p.name}</div>
          <div className="text-sm text-slate-400">{p.uhid} · {p.gender}, {p.age}y · {p.mobile}{p.allergies ? <span className="ml-3 text-red-300">⚠ Allergies: {p.allergies}</span> : ""}</div>
        </div>
        <div className="text-right text-sm"><div className="text-slate-400 text-xs">Doctor</div>{visit.doctor_name || "—"}</div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={printRx} className="bg-transparent text-white border-slate-600 hover:bg-slate-800" data-testid="print-rx-button"><Printer className="h-4 w-4 mr-1.5" /> Print Rx</Button>
          {editable && <Button onClick={() => save(false)} disabled={saving} className="bg-white text-slate-900 hover:bg-slate-100" data-testid="save-consultation-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save</>}</Button>}
          {editable && <Button onClick={() => save(true)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="complete-visit-button"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Complete</Button>}
        </div>
      </div>

      <div className="space-y-5" data-testid="consultation-sections">
        <fieldset disabled={!editable} className="disabled:opacity-70 space-y-5 border-0 p-0 m-0">

          <section className="rounded-md p-6 border border-indigo-200 bg-indigo-50/50" data-testid="ai-drafter">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2 text-indigo-700"><Sparkles className="h-4 w-4" /><span className="text-[11px] font-mono uppercase tracking-wider">AI Note Drafter</span></div>
              <span className="text-[11px] font-medium text-indigo-700 bg-white border border-indigo-200 rounded-full px-2.5 py-0.5">AI-generated draft — review before saving</span>
            </div>
            <p className="text-sm text-slate-600 mb-2.5">Type rough inputs (symptoms, findings, keywords) and let AI structure a note. Nothing is saved until you insert and Save.</p>
            <Textarea rows={3} value={aiInput} onChange={(e) => setAiInput(e.target.value)} placeholder="e.g. 3 days fever & sore throat, no cough, temp 100.8, throat congested, likely viral pharyngitis, advise fluids & rest" data-testid="ai-draft-input" className="bg-white" />
            <div className="flex flex-wrap items-center gap-2 mt-2.5">
              <Button type="button" onClick={draftWithAi} disabled={aiStreaming} className="bg-indigo-600 hover:bg-indigo-700" data-testid="ai-draft-btn">
                {aiStreaming ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Drafting…</> : <><Sparkles className="h-4 w-4 mr-1.5" /> Draft with AI</>}
              </Button>
            </div>
            {(aiDraft || aiStreaming) && (
              <div className="mt-3.5 space-y-2.5">
                <Label className="text-xs text-indigo-700">AI draft (editable)</Label>
                <Textarea rows={8} value={aiDraft} onChange={(e) => setAiDraft(e.target.value)} data-testid="ai-draft-output" className="bg-white font-mono text-[13px]" />
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" onClick={applyDraft} disabled={aiStreaming || !aiDraft.trim()} className="bg-slate-900 hover:bg-slate-800" data-testid="ai-draft-apply"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Insert into consultation</Button>
                  <Button type="button" size="sm" variant="outline" onClick={draftWithAi} disabled={aiStreaming} data-testid="ai-draft-regenerate">Regenerate</Button>
                </div>
                <p className="text-[11px] text-slate-500">AI can make mistakes — verify all clinical details before saving to the record.</p>
              </div>
            )}
          </section>

          <section className="bg-white border border-slate-200 rounded-md p-6" data-testid="vitals-panel">
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-4">Vitals</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[["bp","Blood Pressure","mmHg"],["pulse","Pulse","bpm"],["temp","Temperature","°F"],["spo2","SpO₂","%"],["rr","Resp. Rate","/min"],["weight","Weight","kg"],["height","Height","cm"]].map(([k,l,u]) => (
                <div key={k} className="space-y-1.5"><Label className="text-xs">{l} <span className="text-slate-400">({u})</span></Label><Input value={c.vitals[k]} onChange={(e) => setVital(k, e.target.value)} data-testid={`vital-${k}`} /></div>
              ))}
              <div className="space-y-1.5"><Label className="text-xs">BMI</Label><Input value={c.vitals.bmi} readOnly className="bg-slate-50" data-testid="vital-bmi" /></div>
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-md p-6 space-y-4" data-testid="clinical-panel">
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400">Complaints &amp; History</div>
            <div className="space-y-1.5"><Label>Chief complaint</Label><Input value={c.chief_complaint} onChange={(e) => setC({ ...c, chief_complaint: e.target.value })} data-testid="cc-input" /></div>
            <div className="space-y-1.5"><Label>History of present illness</Label><Textarea rows={3} value={c.history_present_illness} onChange={(e) => setC({ ...c, history_present_illness: e.target.value })} data-testid="hpi-input" /></div>
            <div className="space-y-1.5"><Label>Past history (medical / surgical / family / medications)</Label><Textarea rows={4} value={c.past_history} onChange={(e) => setC({ ...c, past_history: e.target.value })} data-testid="past-history-input" /></div>
          </section>

          <section className="bg-white border border-slate-200 rounded-md p-6 space-y-4" data-testid="exam-panel">
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400">Examination</div>
            <div className="space-y-1.5"><Label>Examination findings</Label><Textarea rows={6} value={c.examination} onChange={(e) => setC({ ...c, examination: e.target.value })} data-testid="examination-input" /></div>
            <div className="space-y-1.5"><Label>Clinical notes</Label><Textarea rows={3} value={c.notes} onChange={(e) => setC({ ...c, notes: e.target.value })} /></div>
          </section>

          <section className="bg-white border border-slate-200 rounded-md p-6 space-y-5" data-testid="rx-panel">
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400">Diagnosis &amp; Prescription</div>
            <div>
              <Label>Diagnosis</Label>
              <div className="flex gap-2 mt-1.5"><Input value={dx} onChange={(e) => setDx(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addDx())} placeholder="Add diagnosis (ICD-10 or text)" data-testid="diagnosis-input" /><Button type="button" onClick={addDx} variant="outline" data-testid="add-diagnosis"><Plus className="h-4 w-4" /></Button></div>
              <div className="flex flex-wrap gap-2 mt-2">
                {c.diagnosis.map((d, i) => (<span key={i} className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded px-2 py-1 text-sm">{d}<button onClick={() => setC({ ...c, diagnosis: c.diagnosis.filter((_, idx) => idx !== i) })}><X className="h-3 w-3" /></button></span>))}
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2"><Label>Prescription</Label>
                <div className="flex items-center gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={runMedCheck} disabled={medChecking} className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="med-check-btn">
                    {medChecking ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Checking…</> : <><ShieldCheck className="h-4 w-4 mr-1.5" /> AI safety check</>}
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={addRx} data-testid="add-medicine"><Plus className="h-4 w-4 mr-1.5" /> Add medicine</Button>
                </div>
              </div>
              {medCheck && (
                <div className={`mb-3 rounded-md border p-3 ${medCheck.level === "warning" ? "border-red-200 bg-red-50" : medCheck.level === "caution" ? "border-amber-200 bg-amber-50" : medCheck.level === "none" ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`} data-testid="med-check-result">
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldCheck className={`h-4 w-4 ${medCheck.level === "warning" ? "text-red-600" : medCheck.level === "caution" ? "text-amber-600" : medCheck.level === "none" ? "text-emerald-600" : "text-slate-500"}`} />
                    <span className="text-[11px] font-mono uppercase tracking-wider text-slate-500">AI advisory · {medCheck.level}</span>
                  </div>
                  {medCheck.summary && <p className="text-sm text-slate-700" data-testid="med-check-summary">{medCheck.summary}</p>}
                  {(medCheck.flags || []).length > 0 && (
                    <ul className="mt-2 space-y-1.5">
                      {medCheck.flags.map((f, i) => (
                        <li key={i} className="text-sm flex gap-2" data-testid={`med-check-flag-${i}`}>
                          <span className={`shrink-0 mt-0.5 text-[10px] font-semibold uppercase rounded px-1.5 py-0.5 ${f.severity === "warning" ? "bg-red-100 text-red-700" : f.severity === "caution" ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-600"}`}>{f.severity}</span>
                          <span className="text-slate-700"><span className="font-medium">{(f.medicines || []).join(" + ")}</span>{(f.medicines || []).length ? ": " : ""}{f.issue}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <p className="text-[11px] text-slate-500 mt-2">AI advisory only — use your clinical judgement.</p>
                </div>
              )}
              <div className="space-y-2">
                {c.prescription.map((r, i) => (
                  <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2.5" data-testid={`rx-row-${i}`}>
                    <div className="flex items-center gap-2">
                      <Input list="opd-drugs" placeholder="Medicine name (type to see remembered)" value={r.drug} onChange={(e) => setRx(i, "drug", e.target.value)} data-testid={`rx-drug-${i}`} className="flex-1 font-medium" />
                      <Button type="button" variant="ghost" size="icon" onClick={() => delRx(i)} className="text-red-500 shrink-0" data-testid={`rx-del-${i}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <Input placeholder="Dose (500mg)" value={r.dose} onChange={(e) => setRx(i, "dose", e.target.value)} data-testid={`rx-dose-${i}`} />
                      <Input placeholder="Freq (0-1-0)" value={r.frequency} onChange={(e) => setRx(i, "frequency", e.target.value)} data-testid={`rx-freq-${i}`} />
                      <Input placeholder="Duration (5 days)" value={r.duration} onChange={(e) => setRx(i, "duration", e.target.value)} data-testid={`rx-dur-${i}`} />
                      <Input placeholder="Instructions" value={r.instructions} onChange={(e) => setRx(i, "instructions", e.target.value)} data-testid={`rx-instr-${i}`} />
                    </div>
                    {editable && <div className="flex flex-wrap gap-1.5">
                      {FREQ_PRESETS.map((f) => (
                        <button key={f} type="button" onClick={() => setRx(i, "frequency", f)} className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${r.frequency === f ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-400"}`} data-testid={`rx-freq-chip-${i}-${f}`}>{f}</button>
                      ))}
                    </div>}
                  </div>
                ))}
                {c.prescription.length === 0 && <div className="text-sm text-slate-400">No medicines added yet.</div>}
                <datalist id="opd-drugs">{drugs.map((d) => <option key={d} value={d} />)}</datalist>
              </div>
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-md p-6 space-y-4" data-testid="orders-referrals-panel">
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400">Orders &amp; Referrals</div>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700"><Network className="h-3.5 w-3.5" /> CareCompare</span>
            </div>
            <p className="text-sm text-slate-500">Order labs, imaging and referrals through the CareCompare network.</p>
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={openCareCompare} data-testid="order-lab-btn"><FlaskConical className="h-4 w-4 mr-1.5" /> Order Lab</Button>
              <Button type="button" variant="outline" onClick={openCareCompare} data-testid="order-imaging-btn"><Scan className="h-4 w-4 mr-1.5" /> Order Imaging</Button>
              <Button type="button" variant="outline" onClick={openCareCompare} data-testid="refer-btn"><UserPlus className="h-4 w-4 mr-1.5" /> Refer</Button>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-slate-400"><Network className="h-3.5 w-3.5" /> Opens the CareCompare network at carecompare.in in a new tab.</p>

            <div className="border-t border-slate-100 pt-4 space-y-3">
              <div className="flex items-center justify-between"><Label className="text-xs text-slate-500">Recorded referrals (saved to the visit)</Label><Button type="button" size="sm" variant="ghost" onClick={addRef} data-testid="add-referral"><Plus className="h-4 w-4 mr-1.5" /> Add referral</Button></div>
              {c.referrals.map((r, i) => (
                <div key={i} className="grid grid-cols-[1fr_1.5fr_2fr_auto] gap-2 items-center" data-testid={`referral-row-${i}`}>
                  <select className="h-9 rounded-md border border-slate-300 px-2 text-sm bg-white" value={r.type} onChange={(e) => setRef(i, "type", e.target.value)} data-testid={`referral-type-${i}`}>
                    {REFERRAL_TYPES.map((t) => <option key={t}>{t}</option>)}
                  </select>
                  <Input placeholder="Target (dept/lab/facility)" value={r.target} onChange={(e) => setRef(i, "target", e.target.value)} />
                  <Input placeholder="Notes / tests" value={r.notes} onChange={(e) => setRef(i, "notes", e.target.value)} />
                  <Button type="button" variant="ghost" size="icon" onClick={() => delRef(i)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              {c.referrals.length === 0 && <div className="text-sm text-slate-400">No referrals recorded.</div>}
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-md p-6 space-y-4 max-w-lg" data-testid="followup-panel">
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400">Follow-up</div>
            <div className="space-y-1.5"><Label>Follow-up date</Label><Input type="date" value={c.follow_up_date || ""} onChange={(e) => setC({ ...c, follow_up_date: e.target.value })} data-testid="followup-date-input" /></div>
            <div className="space-y-1.5"><Label>Follow-up time</Label><Input type="time" value={c.follow_up_time || ""} onChange={(e) => setC({ ...c, follow_up_time: e.target.value })} data-testid="followup-time-input" /></div>
            <div className="space-y-1.5"><Label>Advice</Label><Textarea rows={3} value={c.follow_up_advice} onChange={(e) => setC({ ...c, follow_up_advice: e.target.value })} data-testid="followup-advice-input" /></div>
            <div className="flex items-center gap-2 text-sm text-slate-500"><Activity className="h-4 w-4 text-blue-500" /> Setting a date adds this patient to the Follow-ups reminders list.</div>
          </section>

        </fieldset>
      </div>
    </div>
  );
}
